import {
  greetingByIdQuery,
  greetingCreate,
  greetingUpdate,
} from '@/entities/greetings'
import type { CreateOrUpdateGreetingModel, GreetingModel } from '@/shared/api'
import { createModalModel } from '@/shared/lib/model-factories'
import { createEvent, sample } from 'effector'
import { createGate } from 'effector-react'
import { or } from 'patronum'

// Initialize

type GreetingFormGateProps = {
  isModal?: boolean
  onSubmitted?: (id: string) => void
  resetForm?: () => void
}

export const GreetingFormGate = createGate<GreetingFormGateProps>()

// Modal

export const {
  modalOpened: greetingFormModalOpened,
  modalClosed: greetingFormModalClosed,
  $isModalOpened: $isGreetingFormModalOpened,
} = createModalModel()

const greetingFormModalOpenedForEdit = createEvent<{ id: string }>()
sample({
  clock: greetingFormModalOpened,
  filter: (params): params is { id: string } => Boolean(params),
  target: greetingFormModalOpenedForEdit,
})

sample({
  clock: greetingFormModalOpenedForEdit,
  target: greetingByIdQuery.start,
})
sample({
  source: GreetingFormGate.state,
  clock: greetingFormModalClosed,
  fn: (state) => {
    state.resetForm?.()
  },
})

sample({
  source: greetingByIdQuery.$data,
  clock: greetingFormModalClosed,
  target: greetingByIdQuery.reset,
})
// Submit

export const $submittingGreeting = or(
  greetingCreate.$pending,
  greetingUpdate.$pending,
)

export const greetingSubmitted = createEvent<CreateOrUpdateGreetingModel>()
sample({
  source: greetingByIdQuery.$data,
  clock: greetingSubmitted,
  filter: (greeting: GreetingModel | null): greeting is null =>
    greeting === null,
  fn: (
    _,
    greetingSubmittedData,
  ): {
    data: CreateOrUpdateGreetingModel
  } => {
    return { data: greetingSubmittedData }
  },
  target: greetingCreate.start,
})

sample({
  source: greetingByIdQuery.$data,
  clock: greetingSubmitted,
  filter: (greeting: GreetingModel | null): greeting is GreetingModel =>
    greeting !== null && typeof greeting.id === 'string',
  fn: (greeting, greetingSubmittedData) => {
    return {
      id: greeting.id,
      data: greetingSubmittedData,
    }
  },
  target: greetingUpdate.start,
})

// OnSubmitted

sample({
  clock: [greetingCreate.finished.success, greetingUpdate.finished.success],
  source: GreetingFormGate.state,
  filter: (gateState) => Boolean(gateState.isModal),
  fn: (gateState, { result }) => {
    if (result?.id && gateState.onSubmitted) {
      gateState.onSubmitted(result.id)
    }
  },
  target: greetingFormModalClosed,
})
